import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Heart, ShieldCheck, Truck, Store, Factory, Briefcase, DollarSign } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const solutionCards = [
  {
    id: 'ai-strategy',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80',
    title: 'AI Strategy & Consulting',
    shortDescription: 'Define your AI roadmap, identify high-impact opportunities, and ensure every AI investment delivers maximum ROI.',
    fullDetailsHeading: 'Strategic AI Roadmapping & Opportunity Identification',
    fullDetailsParagraph1: 'Our AI Strategy & Consulting services are designed to guide B2B enterprises through the complexities of AI adoption. We work closely with your leadership to understand your unique business landscape, identify critical pain points, and pinpoint high-value AI opportunities that promise significant returns.',
    fullDetailsParagraph2: 'We develop a clear, actionable AI roadmap tailored to your organization, prioritizing initiatives that align with your strategic objectives and ensuring a phased, manageable implementation. Our focus is on building an AI foundation that is scalable, sustainable, and directly contributes to your competitive advantage.',
    benefits: [
      'Maximized ROI from AI investments.',
      'Clear, actionable AI roadmap aligned with business goals.',
      'Identification of high-impact AI use cases.',
      'Mitigation of AI implementation risks.',
      'Enhanced data-driven decision-making capabilities.'
    ],
    ctaText: 'Request an AI Strategy Session'
  },
  {
    id: 'ai-knowledge-hubs',
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&q=80',
    title: 'AI Knowledge Hubs',
    shortDescription: 'Centralize enterprise knowledge, empower employees, and prevent information silos with intelligent AI-powered platforms.',
    fullDetailsHeading: 'Intelligent Knowledge Management & Collaboration',
    fullDetailsParagraph1: 'Our AI Knowledge Hub solutions transform fragmented information into a unified, accessible, and intelligent resource. By leveraging advanced AI, we create platforms that automatically catalog, organize, and retrieve critical business knowledge, making it instantly available to your entire workforce.',
    fullDetailsParagraph2: 'This empowers employees with on-demand access to expertise, reduces redundant efforts, and fosters a culture of continuous learning and collaboration. Safeguard your institutional memory and accelerate decision-making with a centralized, AI-driven knowledge base.',
    benefits: [
      'Centralized and accessible enterprise knowledge.',
      'Increased employee productivity and efficiency.',
      'Reduced information silos and improved collaboration.',
      'Preservation of institutional expertise.',
      'Faster, more informed decision-making.'
    ],
    ctaText: 'Learn About Knowledge Hubs'
  },
  {
    id: 'intelligent-automation',
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80',
    title: 'Intelligent Automation',
    shortDescription: 'Automate repetitive tasks, streamline workflows, and boost operational efficiency across your business functions.',
    fullDetailsHeading: 'Streamlining Operations with AI-Powered Automation',
    fullDetailsParagraph1: 'Our Intelligent Automation services leverage Robotic Process Automation (RPA) combined with advanced AI (like machine learning and natural language processing) to automate complex, rule-based, and cognitive tasks across your enterprise. This frees your human workforce from mundane, high-volume activities.',
    fullDetailsParagraph2: 'We design and implement end-to-end automated workflows that reduce operational costs, minimize human error, and significantly accelerate processing times. From back-office functions to customer service, intelligent automation drives unparalleled efficiency and scalability.',
    benefits: [
      'Significant reduction in operational costs.',
      'Drastically improved process efficiency and speed.',
      'Minimized human error and enhanced data accuracy.',
      'Reallocation of human resources to strategic tasks.',
      'Scalable operations without proportional cost increases.'
    ],
    ctaText: 'Discover Automation Solutions'
  },
  {
    id: 'ai-agents',
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&q=80',
    title: 'AI Agents (Voice & Chat)',
    shortDescription: 'Deploy intelligent virtual agents for sales, customer support, and operations, scaling engagement without increasing overhead.',
    fullDetailsHeading: 'Enhancing Customer & Operational Engagement with AI Agents',
    fullDetailsParagraph1: 'Agility develops and deploys sophisticated AI-driven voice and chat agents that seamlessly integrate into your sales, customer support, and internal operational workflows. These intelligent agents are designed to handle a vast array of inquiries and tasks, providing instant, accurate responses 24/7.',
    fullDetailsParagraph2: 'By automating routine interactions and providing immediate assistance, our AI agents dramatically reduce response times, improve customer satisfaction, and enable your business to scale its engagement capabilities exponentially without the traditional costs associated with human resource expansion.',
    benefits: [
      '24/7 instant customer support and sales assistance.',
      'Significant reduction in operational costs for customer service.',
      'Improved customer satisfaction through rapid, accurate responses.',
      'Seamless scalability of customer and sales operations.',
      'Freeing human agents for complex, high-value interactions.'
    ],
    ctaText: 'Explore AI Agent Capabilities'
  },
  {
    id: 'ai-product-integration',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80',
    title: 'AI-Powered Product Integration',
    shortDescription: 'Infuse your existing products with advanced AI capabilities, creating smarter, more adaptive, and market-leading solutions.',
    fullDetailsHeading: 'Elevating Products with Embedded AI Intelligence',
    fullDetailsParagraph1: 'In today\'s competitive landscape, product innovation is key. Agility specializes in seamlessly embedding advanced AI capabilities directly into your existing product portfolio, transforming them into intelligent, adaptive, and highly differentiated solutions.',
    fullDetailsParagraph2: 'Whether it\'s predictive analytics for enhanced user experience, natural language interfaces for intuitive interaction, or machine learning for personalized recommendations, we empower your products to learn, evolve, and deliver unparalleled value, ensuring market leadership and increased user engagement.',
    benefits: [
      'Creation of smarter, more intuitive, and adaptive products.',
      'Enhanced user experience and increased customer satisfaction.',
      'Competitive differentiation and market leadership.',
      'New revenue streams through intelligent product features.',
      'Products that continuously learn and improve over time.'
    ],
    ctaText: 'Integrate AI into Your Product'
  },
  {
    id: 'predictive-analytics',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80',
    title: 'Predictive Analytics & Forecasting',
    shortDescription: 'Leverage advanced machine learning to predict market trends, customer behavior, and operational outcomes with unparalleled accuracy.',
    fullDetailsHeading: 'Anticipating the Future with Advanced Predictive AI',
    fullDetailsParagraph1: 'Our Predictive Analytics & Forecasting solutions empower your business to move beyond reactive decision-making. By applying sophisticated machine learning algorithms to your historical and real-time data, we uncover hidden patterns and forecast future trends with remarkable accuracy.',
    fullDetailsParagraph2: 'This enables proactive strategies in areas like demand planning, customer churn prediction, fraud detection, and resource allocation. Gain a significant competitive edge by anticipating market shifts and optimizing your operations based on intelligent, data-driven predictions.',
    benefits: [
      'Highly accurate predictions for key business metrics.',
      'Proactive decision-making and strategic planning.',
      'Optimization of resources and operational efficiency.',
      'Early detection of risks and opportunities.',
      'Enhanced competitive advantage through foresight.'
    ],
    ctaText: 'Get Predictive Insights'
  },
  {
    id: 'data-engineering',
    image: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?auto=format&fit=crop&q=80',
    title: 'Data Engineering & Architecture',
    shortDescription: 'Build robust, scalable data pipelines and infrastructure that ensure clean, reliable data for all your AI and analytics initiatives.',
    fullDetailsHeading: 'Building the Foundation for Data-Driven Success',
    fullDetailsParagraph1: 'The success of any AI or analytics initiative hinges on clean, reliable, and accessible data. Our Data Engineering & Architecture services focus on designing, building, and optimizing robust data pipelines and infrastructure that can handle vast volumes of complex data.',
    fullDetailsParagraph2: 'We ensure data quality, implement efficient ETL/ELT processes, and establish scalable data lakes and warehouses. This foundational work guarantees that your AI models have the high-quality data they need to deliver accurate insights and drive transformative business outcomes.',
    benefits: [
      'Clean, reliable, and accessible data for AI/analytics.',
      'Robust and scalable data infrastructure.',
      'Efficient data processing and integration.',
      'Improved data quality and governance.',
      'Accelerated development and deployment of AI solutions.'
    ],
    ctaText: 'Optimize Your Data Infrastructure'
  },
  {
    id: 'web-mobile-development',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80',
    title: 'Web & Mobile Development',
    shortDescription: 'Custom web and mobile applications built with modern frameworks, delivering seamless user experiences across all platforms.',
    fullDetailsHeading: 'Building Modern, Scalable Applications',
    fullDetailsParagraph1: 'Our Web & Mobile Development services deliver custom applications that meet your unique business needs. Using modern frameworks like React, React Native, and Flutter, we create responsive web applications and cross-platform mobile apps that work seamlessly across devices.',
    fullDetailsParagraph2: 'From initial design to deployment and ongoing support, we ensure your applications are built with scalability, performance, and user experience at the forefront. Whether you need a customer-facing mobile app or an internal web platform, we have the expertise to bring your vision to life.',
    benefits: [
      'Modern, responsive applications for web and mobile.',
      'Cross-platform development for maximum reach.',
      'Scalable architecture built for growth.',
      'Intuitive user interfaces backed by UX research.',
      'Ongoing support and maintenance.'
    ],
    ctaText: 'Start Your App Project'
  }
];

const industries = [
  { icon: Heart, title: 'Healthcare', description: 'AI-driven patient care and operational efficiency' },
  { icon: Store, title: 'Retail', description: 'Smart inventory and customer experience optimization' },
  { icon: ShieldCheck, title: 'Insurance', description: 'Automated claims processing and risk assessment' },
  { icon: DollarSign, title: 'Fintech', description: 'Fraud detection and personalized financial services' },
  { icon: Factory, title: 'Manufacturing', description: 'Predictive maintenance and production optimization' },
  { icon: Truck, title: 'Logistics', description: 'Supply chain optimization and route planning' },
  { icon: Building2, title: 'Real Estate', description: 'Smart building management and predictive analytics' },
  { icon: Briefcase, title: 'Professional Services', description: 'Workflow automation and resource optimization' }
];

const Solutions = () => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const toggleCard = (cardId: string) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  return (
    <>
      <SEOHead
        title="AI Solutions & Services | Agility"
        description="Comprehensive AI consulting, automation, data engineering, and web development solutions for enterprises."
        url="https://www.agilitytech.ai/solutions"
      />
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
            alt="Solutions Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/80 to-black/70" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Transforming Enterprises with Intelligent AI Solutions
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Agility delivers bespoke AI strategies and cutting-edge solutions designed to solve your most complex business challenges, drive efficiency, and unlock new growth opportunities.
            </p>
            <Link
              to="/contact"
              className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium 
                hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get a Custom AI Solution</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Our Holistic AI Solution Approach
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              At Agility, we believe in a partnership-driven approach to AI. We don't just implement technology; we integrate intelligent solutions that align with your strategic goals, ensuring seamless adoption and measurable impact. Our process involves deep understanding of your business, agile development, and continuous optimization.
            </p>
          </div>
        </div>
      </section>

      {/* Core Solution Areas */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Core Solution Areas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutionCards.map((solution) => (
              <div
                key={solution.id}
                className="solution-card bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1 hover:shadow-xl cursor-pointer"
                onClick={() => toggleCard(solution.id)}
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden mb-6">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{solution.title}</h3>
                <p className="text-gray-300 mb-6">{solution.shortDescription}</p>
                
                <div className={`solution-full-details transition-all duration-500 ease-in-out overflow-hidden ${
                  expandedCard === solution.id ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="border-t border-gray-700 pt-6 mt-6">
                    <h4 className="text-lg font-bold text-white mb-4">{solution.fullDetailsHeading}</h4>
                    <p className="text-gray-300 mb-4 leading-relaxed">{solution.fullDetailsParagraph1}</p>
                    <p className="text-gray-300 mb-6 leading-relaxed">{solution.fullDetailsParagraph2}</p>
                    
                    <h5 className="text-md font-semibold text-teal-400 mb-4">Key Benefits:</h5>
                    <ul className="space-y-2 mb-6">
                      {solution.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-gray-300">
                          <ArrowRight className="h-4 w-4 text-teal-400 mt-1 flex-shrink-0" />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link
                      to="/contact"
                      className="inline-flex items-center space-x-2 bg-teal-500 text-white px-6 py-3 
                        rounded-lg font-medium hover:bg-teal-400 transition-colors"
                    >
                      <span>{solution.ctaText}</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
                
                <button className="text-teal-400 font-medium flex items-center space-x-2 
                  hover:text-teal-300 transition-colors">
                  <span>{expandedCard === solution.id ? 'Show Less' : 'Learn More'}</span>
                  <ArrowRight 
                    className={`h-4 w-4 transition-transform duration-300 ${
                      expandedCard === solution.id ? 'rotate-90' : ''
                    }`} 
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              AI Solutions Across Key Industries
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our AI solutions are tailored to meet the unique challenges and opportunities within specific industries, delivering targeted value and measurable outcomes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="industry-card bg-gray-800/50 rounded-xl p-6 text-center
                  hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1
                  border border-gray-700/50 hover:border-gray-600/50"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mx-auto mb-4">
                  <industry.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.title}</h3>
                <p className="text-gray-300 text-sm">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business with AI?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Partner with Agility to unlock the full potential of artificial intelligence for your enterprise. Let's build intelligent solutions that drive growth, efficiency, and competitive advantage.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Start Your AI Journey Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Solutions;